// Stok kartı "Ambalaj ve barkodlar": kutu/paket/koli/palet tanımları, içerikleri, ambalaj fiyatı ve barkodları.
// Kayıt kartla birlikte yapılır (handle: validate → preflight → save / saveFor).
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, IconButton,
  InputAdornment, MenuItem, Paper, TextField, Tooltip, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { useTranslation } from 'react-i18next';
import {
  allBarcodes, computeFactors, effectivePrice, fingerprint, fold, formatQty, innerUnitOf, isEmptyDraft,
  isValidBarcodeText, MAX_PACKAGING_ROWS, sameName, toSavePayload, validateDraft,
  type PackagingDraft, type PackagingDraftRow,
} from './packagingDraft';
import {
  describeConflict, getPackaging, PackagingError, resolveBarcodes, savePackaging, type PackagingCard,
} from '../../services/packagingService';

const PACKAGE_SUGGESTIONS = ['Kutu', 'Paket', 'Koli', 'Palet', 'Çuval', 'Kasa', 'Teneke', 'Bidon', 'Deste', 'Şişe', 'Rulo'];
const EMPTY: PackagingDraft = { rows: [], baseBarcodes: [] };

export interface PackagingEditorHandle {
  /** Taslak hatası (Türkçe) ya da null. Hatalı satırları işaretler. */
  validate: () => string | null;
  isDirty: () => boolean;
  /** Barkodların başka kartta kullanılıp kullanılmadığını sunucuya sorar; çakışma mesajı ya da null. */
  preflight: (ownItemId?: string | null) => Promise<string | null>;
  /** Düzenlenen kart için kaydeder (değişiklik yoksa hiçbir şey yapmaz). */
  save: () => Promise<string[]>;
  /** Yeni oluşturulan kart için kaydeder (taslak boşsa hiçbir şey yapmaz). */
  saveFor: (itemId: string) => Promise<string[]>;
}

interface PackagingEditorProps {
  itemId?: string | null;
  baseUnit: string;
  mainBarcode: string;
  itemSalePrice: number;
  productionEnabled: boolean;
  onBaseUnitLocked?: (locked: boolean) => void;
}

function newKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `row-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function fromCard(card: PackagingCard): PackagingDraft {
  return {
    baseBarcodes: card.baseBarcodes,
    rows: card.units.map((unit) => ({
      key: unit.id || newKey(),
      id: unit.id,
      unitName: unit.unitName,
      // İçeriği olmayan eski birim: "1 koli = 24 adet" olarak gösterilir.
      containsText: formatQty(unit.containsQuantity ?? unit.factor).replace(/\./g, ''),
      containsUnit: unit.containsQuantity != null ? (unit.containsUnitName ?? '') : '',
      salePriceText: unit.salePrice != null ? String(unit.salePrice).replace('.', ',') : '',
      isDefaultForProduction: unit.isDefaultForProduction,
      recipeUsageCount: unit.recipeUsageCount,
      barcodes: unit.barcodes,
    })),
  };
}

/** Okuyucuyla okutulabilen barkod listesi: Enter (okuyucu gönderir) ya da odak çıkışı ekler. */
const BarcodeChips: React.FC<{
  id: string;
  codes: string[];
  onChange: (codes: string[]) => void;
  placeholder: string;
}> = ({ id, codes, onChange, placeholder }) => {
  const [value, setValue] = useState('');
  const [hint, setHint] = useState<string | null>(null);

  const commit = () => {
    const code = value.trim();
    if (!code) return;
    if (!isValidBarcodeText(code)) {
      setHint('Barkod boşluk içeremez ve en fazla 100 karakter olabilir.');
      return;
    }
    if (codes.some((c) => c.toLowerCase() === code.toLowerCase())) {
      setHint(`'${code}' zaten listede.`);
      setValue('');
      return;
    }
    onChange([...codes, code]);
    setValue('');
    setHint(null);
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', minWidth: 0 }}>
      {codes.map((code) => (
        <Chip
          key={code}
          label={code}
          size="small"
          onDelete={() => onChange(codes.filter((c) => c !== code))}
          sx={{ fontFamily: 'monospace' }}
        />
      ))}
      <TextField
        id={id}
        size="small"
        value={value}
        placeholder={placeholder}
        onChange={(e) => { setValue(e.target.value); setHint(null); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
        onBlur={commit}
        error={!!hint}
        helperText={hint || undefined}
        sx={{ minWidth: 200, flex: '1 1 200px', maxWidth: 320 }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><QrCodeScannerIcon fontSize="small" /></InputAdornment>,
        }}
      />
    </Box>
  );
};

const PackagingEditor = forwardRef<PackagingEditorHandle, PackagingEditorProps>(({
  itemId, baseUnit, mainBarcode, itemSalePrice, productionEnabled, onBaseUnitLocked,
}, ref) => {
  const { t } = useTranslation('accounting');
  const [draft, setDraft] = useState<PackagingDraft>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadedBase, setLoadedBase] = useState('');
  const draftRef = useRef(draft);
  const snapshot = useRef(fingerprint(EMPTY));
  draftRef.current = draft;

  useEffect(() => {
    if (!itemId) {
      setDraft(EMPTY);
      setLoadFailed(false);
      setLoadedBase('');
      snapshot.current = fingerprint(EMPTY);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);
    getPackaging(itemId)
      .then((card) => {
        if (cancelled) return;
        const next = fromCard(card);
        setDraft(next);
        setLoadedBase(card.baseUnit);
        snapshot.current = fingerprint(next);
      })
      .catch(() => {
        if (cancelled) return;
        setDraft(EMPTY);
        setLoadFailed(true);
        setLoadedBase('');
        snapshot.current = fingerprint(EMPTY);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [itemId]);

  // Ambalaj tanımlıyken ana birim değiştirilemez (içerikler ana birime göre).
  const locked = draft.rows.length > 0 && loadedBase !== '' && !sameName(loadedBase, baseUnit);
  useEffect(() => {
    onBaseUnitLocked?.(locked);
    return () => onBaseUnitLocked?.(false);
  }, [locked, onBaseUnitLocked]);

  const markInvalid = (keys: Set<string>) =>
    setDraft((current) => ({ ...current, rows: current.rows.map((row) => ({ ...row, invalid: keys.has(row.key) })) }));

  const persist = async (targetId: string): Promise<string[]> => {
    const payload = toSavePayload(draftRef.current, baseUnit);
    try {
      const card = await savePackaging(targetId, payload);
      const next = fromCard(card);
      setDraft(next);
      setLoadedBase(card.baseUnit);
      snapshot.current = fingerprint(next);
      return [];
    } catch (error) {
      if (error instanceof PackagingError && error.code === 'BARCODE_CONFLICT' && error.conflicts.length > 0) {
        const codes = new Set(error.conflicts.map((c) => c.barcode.toLowerCase()));
        markInvalid(new Set(draftRef.current.rows
          .filter((row) => row.barcodes.some((code) => codes.has(code.toLowerCase())))
          .map((row) => row.key)));
      }
      throw error;
    }
  };

  useImperativeHandle(ref, () => ({
    validate: () => {
      if (loadFailed) return null;
      const result = validateDraft(draftRef.current, baseUnit, mainBarcode);
      markInvalid(result.invalidKeys);
      return result.message;
    },
    isDirty: () => fingerprint(draftRef.current) !== snapshot.current,
    preflight: async (ownItemId) => {
      const codes = allBarcodes(draftRef.current);
      if (codes.length === 0) return null;
      try {
        const results = await resolveBarcodes(codes);
        for (const result of results) {
          const foreign = result.matches.find((m) => !ownItemId || m.itemId.toLowerCase() !== ownItemId.toLowerCase());
          if (foreign) return describeConflict(result.code, { itemName: foreign.itemName, unitName: foreign.unitName });
        }
        return null;
      } catch {
        return null; // sunucu kayıtta yine kontrol eder
      }
    },
    save: async () => {
      if (!itemId || loadFailed || fingerprint(draftRef.current) === snapshot.current) return [];
      return persist(itemId);
    },
    saveFor: async (newItemId) => {
      if (isEmptyDraft(draftRef.current)) return [];
      return persist(newItemId);
    },
  }), [baseUnit, itemId, loadFailed, mainBarcode]); // eslint-disable-line react-hooks/exhaustive-deps

  const factors = useMemo(() => computeFactors(draft.rows, baseUnit), [draft.rows, baseUnit]);
  const baseLabel = baseUnit || 'adet';

  const updateRow = (key: string, patch: Partial<PackagingDraftRow>) =>
    setDraft((current) => ({
      ...current,
      rows: current.rows.map((row) => (row.key === key ? { ...row, ...patch, invalid: false } : row)),
    }));

  const removeRow = (key: string) =>
    setDraft((current) => {
      const removed = current.rows.find((row) => row.key === key);
      return {
        ...current,
        // İçinde bu ambalaj olan satırlar ana birime döner.
        rows: current.rows
          .filter((row) => row.key !== key)
          .map((row) => (removed && sameName(row.containsUnit, removed.unitName) ? { ...row, containsUnit: '' } : row)),
      };
    });

  const addRow = () =>
    setDraft((current) => ({
      ...current,
      rows: [...current.rows, {
        key: newKey(), unitName: '', containsText: '', containsUnit: '', salePriceText: '',
        isDefaultForProduction: false, recipeUsageCount: 0, barcodes: [],
      }],
    }));

  const tree = useMemo(() => draft.rows
    .filter((row) => factors.has(fold(row.unitName)))
    .map((row) => ({ row, factor: factors.get(fold(row.unitName))! }))
    .sort((a, b) => b.factor - a.factor), [draft.rows, factors]);

  if (loading) return <CircularProgress size={22} />;
  if (loadFailed) {
    return <Alert severity="warning">{t('stock.packaging.loadFailed', 'Ambalajlar yüklenemedi. Kart kaydedilirse ambalajlara dokunulmaz.')}</Alert>;
  }

  const price = (n: number) => n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      {locked && (
        <Alert severity="warning">
          {t('stock.packaging.baseUnitLocked', 'Ambalajı olan kartın ana birimi değiştirilemez; önce ambalajları kaldırın.')}
        </Alert>
      )}

      <Box>
        <Typography variant="subtitle2">
          {t('stock.packaging.baseBarcodesTitle', 'Ana birim ({{unit}}) için ek barkodlar', { unit: baseLabel })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('stock.packaging.baseBarcodesHint', 'Tedarikçi barkodu değiştiyse eski barkod da okunmaya devam etsin diye buraya ekleyin. Kartın asıl barkodu üstteki Barkod alanındadır.')}
        </Typography>
        <BarcodeChips
          id="stock-card-base-barcode"
          codes={draft.baseBarcodes}
          onChange={(codes) => setDraft((current) => ({ ...current, baseBarcodes: codes }))}
          placeholder={t('stock.packaging.scanPlaceholder', 'Barkod okutun veya yazıp Enter')}
        />
      </Box>

      <Box sx={{ display: 'grid', gap: 1.25 }}>
        <Typography variant="subtitle2">{t('stock.packaging.unitsTitle', 'Ambalajlar')}</Typography>
        {draft.rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('stock.packaging.empty', 'Henüz ambalaj yok. Örnek: 1 Paket = 6 {{unit}}, 1 Koli = 4 Paket.', { unit: baseLabel })}
          </Typography>
        )}

        {draft.rows.map((row, index) => {
          const factor = factors.get(fold(row.unitName));
          const auto = effectivePrice({ ...row, salePriceText: '' }, factor, itemSalePrice);
          const innerOptions = draft.rows.filter((other) => other.key !== row.key && other.unitName.trim());
          return (
            <Paper
              key={row.key}
              variant="outlined"
              sx={{
                p: 1.5, display: 'grid', gap: 1.25,
                bgcolor: row.invalid ? (th) => alpha(th.palette.error.light, 0.18) : undefined,
                borderColor: row.invalid ? 'error.main' : undefined,
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={PACKAGE_SUGGESTIONS.filter((name) => !sameName(name, baseUnit))}
                  inputValue={row.unitName}
                  onInputChange={(_, value) => updateRow(row.key, { unitName: value.slice(0, 20) })}
                  sx={{ width: 160 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id={`packaging-name-${index}`}
                      label={t('stock.packaging.name', 'Ambalaj')}
                      error={row.invalid}
                    />
                  )}
                />
                <Typography variant="body2" color="text.secondary">=</Typography>
                <TextField
                  id={`packaging-qty-${index}`}
                  size="small"
                  label={t('stock.packaging.contains', 'İçeriği')}
                  value={row.containsText}
                  onChange={(e) => updateRow(row.key, { containsText: e.target.value })}
                  inputProps={{ inputMode: 'decimal' }}
                  sx={{ width: 100 }}
                />
                <TextField
                  id={`packaging-inner-${index}`}
                  select
                  size="small"
                  label={t('stock.packaging.innerUnit', 'Birim')}
                  value={innerUnitOf(row, baseUnit) ? row.containsUnit.trim() : ''}
                  onChange={(e) => updateRow(row.key, { containsUnit: e.target.value })}
                  sx={{ width: 130 }}
                >
                  <MenuItem value="">{baseLabel}</MenuItem>
                  {innerOptions.map((other) => (
                    <MenuItem key={other.key} value={other.unitName.trim()}>{other.unitName.trim()}</MenuItem>
                  ))}
                </TextField>
                <Chip
                  size="small"
                  color={factor != null ? 'primary' : 'default'}
                  variant="outlined"
                  label={factor != null ? `= ${formatQty(factor)} ${baseLabel}` : '= ?'}
                />
                <TextField
                  id={`packaging-price-${index}`}
                  size="small"
                  label={t('stock.packaging.salePrice', 'Satış fiyatı')}
                  value={row.salePriceText}
                  onChange={(e) => updateRow(row.key, { salePriceText: e.target.value })}
                  placeholder={auto != null ? price(auto) : ''}
                  helperText={!row.salePriceText.trim() && auto != null
                    ? t('stock.packaging.autoPrice', 'Boş: {{n}} × adet fiyatı', { n: factor != null ? formatQty(factor) : '?' })
                    : undefined}
                  inputProps={{ inputMode: 'decimal' }}
                  InputProps={{ endAdornment: <InputAdornment position="end">₺</InputAdornment> }}
                  sx={{ width: 170 }}
                />
                {productionEnabled && (
                  <FormControlLabel
                    sx={{ ml: 0 }}
                    control={(
                      <Checkbox
                        size="small"
                        checked={row.isDefaultForProduction}
                        onChange={() => setDraft((current) => ({
                          ...current,
                          rows: current.rows.map((item) => ({
                            ...item,
                            isDefaultForProduction: item.key === row.key ? !row.isDefaultForProduction : false,
                            invalid: false,
                          })),
                        }))}
                      />
                    )}
                    label={<Typography variant="caption">{t('stock.packaging.defaultForProduction', 'Üretimde varsayılan')}</Typography>}
                  />
                )}
                {row.recipeUsageCount > 0 && (
                  <Chip size="small" label={t('stock.packaging.recipeUsage', '{{count}} reçetede', { count: row.recipeUsageCount })} />
                )}
                <Box sx={{ flex: 1 }} />
                <Tooltip title={row.recipeUsageCount > 0
                  ? t('stock.packaging.inRecipe', 'Reçetede kullanılan ambalaj silinemez')
                  : t('stock.packaging.remove', 'Ambalajı kaldır')}
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => removeRow(row.key)}
                      disabled={row.recipeUsageCount > 0}
                      aria-label={t('stock.packaging.remove', 'Ambalajı kaldır')}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <BarcodeChips
                id={`packaging-barcode-${index}`}
                codes={row.barcodes}
                onChange={(codes) => updateRow(row.key, { barcodes: codes })}
                placeholder={t('stock.packaging.scanUnitPlaceholder', '{{unit}} barkodu okutun', { unit: row.unitName.trim() || t('stock.packaging.name', 'Ambalaj') })}
              />
            </Paper>
          );
        })}

        <Box>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addRow}
            disabled={draft.rows.length >= MAX_PACKAGING_ROWS}
          >
            {t('stock.packaging.add', 'Ambalaj ekle (Kutu, Paket, Koli, Palet…)')}
          </Button>
        </Box>
      </Box>

      {tree.length > 0 && (
        <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover', display: 'grid', gap: 0.5 }}>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {t('stock.packaging.tree', 'Ambalaj ağacı')}
          </Typography>
          {tree.map(({ row, factor }) => {
            const inner = innerUnitOf(row, baseUnit);
            return (
              <Typography key={row.key} variant="body2">
                1 {row.unitName.trim()} = {row.containsText.trim()} {inner || baseLabel}
                {inner ? <Typography component="span" variant="body2" color="text.secondary">{` (${formatQty(factor)} ${baseLabel})`}</Typography> : null}
                {row.barcodes.length === 0 && (
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' · '}{t('stock.packaging.noBarcode', 'barkod yok')}
                  </Typography>
                )}
              </Typography>
            );
          })}
        </Box>
      )}
    </Box>
  );
});

PackagingEditor.displayName = 'PackagingEditor';

export default PackagingEditor;
